import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  getDoc,
  query,
  where,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../firebase';
import { 
  InvoiceRequest, 
  ProformaRequest, 
  InvoiceListItem,
  InvoiceDetails 
} from '../../types/billing';
import { warrantyService } from '../../services/warrantyService';
import { CreateWarrantyData } from '../../types/warranty';

// Cloud Function references
const createInvoiceFunction = httpsCallable(functions, 'createInvoice');
const createProformaFunction = httpsCallable(functions, 'createProforma');
const downloadInvoiceFunction = httpsCallable(functions, 'downloadInvoice');
const generateHTMLPreviewFunction = httpsCallable(functions, 'generateHTMLPreview');

export const createInvoice = async (invoiceData: InvoiceRequest, organizationId: string): Promise<void> => {
  if (!organizationId) {
    throw new Error('Organization ID not found');
  }

  try {
    console.log('Sending invoice data to Cloud Function:', { invoiceData, organizationId });
    await createInvoiceFunction({ invoiceData, organizationId });
  } catch (error) {
    console.error('Error creating invoice:', error);
    throw new Error(`Failed to create invoice: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const createProforma = async (proformaData: ProformaRequest, organizationId: string): Promise<void> => {
  if (!organizationId) {
    throw new Error('Organization ID not found');
  }

  try {
    await createProformaFunction({ proformaData, organizationId });
  } catch (error) {
    console.error('Error creating proforma:', error);
    throw new Error(`Failed to create proforma: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Helper functions for calculations
const calculateNetAmount = (items: any[]): number => {
  return items.reduce((total, item) => {
    const netValue = item.quantity * item.unitPrice;
    return total + netValue;
  }, 0);
};

const calculateGrossAmount = (items: any[]): number => {
  return items.reduce((total, item) => {
    const netValue = item.quantity * item.unitPrice;
    const taxRate = item.taxRate || 27;
    const grossValue = netValue * (1 + taxRate / 100);
    return total + grossValue;
  }, 0);
};

// Helper function to generate draft number
const generateDraftNumber = async (organizationId: string): Promise<string> => {
  const currentYear = new Date().getFullYear();
  const yearPrefix = `DRAFT-${currentYear}`;
  
  // Get existing drafts for this year to determine next number
  const draftsSnapshot = await getDocs(collection(db, 'invoiceDrafts'));
  const existingDrafts = draftsSnapshot.docs
    .filter(doc => doc.data().organizationId === organizationId)
    .map(doc => doc.data().draftNumber)
    .filter(number => number && number.startsWith(yearPrefix));
  
  // Find the highest number
  let maxNumber = 0;
  existingDrafts.forEach(draftNumber => {
    const numberPart = draftNumber.split('-')[2]; // Get the last part
    const num = parseInt(numberPart);
    if (!isNaN(num) && num > maxNumber) {
      maxNumber = num;
    }
  });
  
  const nextNumber = maxNumber + 1;
  return `${yearPrefix}-${nextNumber.toString().padStart(3, '0')}`;
};

// Helper function to generate warranties for invoice items
export const generateWarrantiesFromInvoiceId = async (
  invoiceId: string,
  organizationId: string
): Promise<void> => {
  try {
    let invoiceDoc;
    let actualInvoiceId = invoiceId;
    
    // Determine the correct collection and document ID based on prefix
    if (invoiceId.startsWith('live_')) {
      // Live invoice - stored in 'invoices' collection
      actualInvoiceId = invoiceId.replace('live_', '');
      const invoiceRef = doc(db, 'invoices', actualInvoiceId);
      invoiceDoc = await getDoc(invoiceRef);
    } else if (invoiceId.startsWith('draft_')) {
      // Draft invoice - stored in 'invoiceDrafts' collection
      actualInvoiceId = invoiceId.replace('draft_', '');
      const invoiceRef = doc(db, 'invoiceDrafts', actualInvoiceId);
      invoiceDoc = await getDoc(invoiceRef);
    } else {
      // Try both collections if no prefix (fallback)
      let invoiceRef = doc(db, 'invoices', invoiceId);
      invoiceDoc = await getDoc(invoiceRef);
      
      if (!invoiceDoc.exists()) {
        invoiceRef = doc(db, 'invoiceDrafts', invoiceId);
        invoiceDoc = await getDoc(invoiceRef);
      }
    }
    
    if (!invoiceDoc.exists()) {
      throw new Error('Invoice not found');
    }
    
    const invoiceData = invoiceDoc.data();
    
    // Call the existing warranty generation function
    await generateWarrantiesForInvoice(actualInvoiceId, invoiceData as InvoiceRequest, organizationId);
  } catch (error) {
    console.error('Error generating warranties from invoice ID:', error);
    throw error;
  }
};

export const generateWarrantiesForInvoice = async (
  invoiceId: string, 
  invoiceData: InvoiceRequest, 
  organizationId: string
): Promise<void> => {
  try {
    // We'll need to find matching products for each item to create warranties
    // For now, we'll create basic warranties based on the invoice items
    const warranties: CreateWarrantyData[] = [];
    
    for (const item of invoiceData.items) {
      // Try to find a matching product by name
      const productsQuery = query(
        collection(db, 'products'),
        where('organizationId', '==', organizationId),
        where('name', '==', item.name),
        limit(1)
      );
      
      const productsSnapshot = await getDocs(productsQuery);
      let productId = '';
      
      if (!productsSnapshot.empty) {
        productId = productsSnapshot.docs[0].id;
      } else {
        // If no matching product found, skip warranty creation for this item
        console.warn(`No product found for item: ${item.name}`);
        continue;
      }
      
      // Try to find customer by name
      const customersQuery = query(
        collection(db, 'customers'),
        where('organizationId', '==', organizationId),
        limit(1) // We'll take the first customer for now, in real implementation we'd match by buyer data
      );
      
      const customersSnapshot = await getDocs(customersQuery);
      let customerId = '';
      
      if (!customersSnapshot.empty) {
        // Try to find exact match first
        const exactMatch = customersSnapshot.docs.find(doc => {
          const customer = doc.data();
          const customerName = customer.type === 'organization' 
            ? customer.companyName 
            : `${customer.firstName} ${customer.lastName}`;
          return customerName === invoiceData.buyer.name;
        });
        
        if (exactMatch) {
          customerId = exactMatch.id;
        } else {
          customerId = customersSnapshot.docs[0].id;
        }
      } else {
        console.warn(`No customer found for buyer: ${invoiceData.buyer.name}`);
        continue;
      }
      
      // Create warranty data
      const warrantyData: CreateWarrantyData = {
        productId,
        customerId,
        type: 'seller', // Default to seller warranty
        startDate: new Date(), // Today
        durationMonths: 12, // Default 1 year warranty
        purchaseDate: new Date(),
        invoiceNumber: `DRAFT-${invoiceId}`, // Use draft ID as invoice reference
        purchasePrice: item.grossValue || (item.quantity * item.unitPrice * (1 + item.taxRate / 100)),
        currency: invoiceData.currency || 'HUF',
        terms: 'Vásárláskor automatikusan generált garancia.',
        coverage: ['Gyártási hibák', 'Anyaghibák'],
        exclusions: ['Helytelen használat', 'Mechanikai sérülések'],
        notes: `Automatikusan generálva a ${invoiceData.buyer.name} vásárlás alapján.`
      };
      
      warranties.push(warrantyData);
    }
    
    // Create all warranties
    for (const warrantyData of warranties) {
      try {
        await warrantyService.createWarranty(warrantyData, organizationId, 'system');
      } catch (error) {
        console.error('Error creating warranty:', error);
        // Continue with other warranties even if one fails
      }
    }
    
    if (warranties.length > 0) {
      console.log(`Generated ${warranties.length} warranties for invoice ${invoiceId}`);
    }
    
  } catch (error) {
    console.error('Error generating warranties for invoice:', error);
    // Don't throw error to prevent invoice creation from failing
  }
};

// Draft management functions
export const saveDraftInvoice = async (invoiceData: InvoiceRequest, organizationId: string): Promise<string> => {
  if (!organizationId) {
    throw new Error('Organization ID not found');
  }

  try {
    const draftNumber = await generateDraftNumber(organizationId);
    
    const draftData = {
      ...invoiceData,
      organizationId,
      draftNumber,
      status: 'DRAFT',
      createdAt: new Date(),
      updatedAt: new Date(),
      invoiceType: 'INVOICE',
      // Flatten buyer data for easier querying
      buyerName: invoiceData.buyer?.name || 'Unknown buyer',
      buyerEmail: invoiceData.buyer?.email || '',
      buyerTaxNumber: invoiceData.buyer?.taxNumber || '',
      buyerPhone: invoiceData.buyer?.phone || '',
      buyerAddress: invoiceData.buyer?.address ? 
        `${invoiceData.buyer.address.street}, ${invoiceData.buyer.address.city}, ${invoiceData.buyer.address.postalCode}` 
        : '',
      // Calculate totals
      netAmount: calculateNetAmount(invoiceData.items),
      grossAmount: calculateGrossAmount(invoiceData.items),
    };

    const docRef = await addDoc(collection(db, 'invoiceDrafts'), draftData);
    
    // Generate warranties if requested
    if (invoiceData.generateWarranty && invoiceData.items && invoiceData.items.length > 0) {
      await generateWarrantiesForInvoice(docRef.id, invoiceData, organizationId);
    }
    
    return docRef.id;
  } catch (error) {
    console.error('Error saving draft invoice:', error);
    throw new Error(`Failed to save draft invoice: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const saveDraftProforma = async (proformaData: ProformaRequest, organizationId: string): Promise<string> => {
  if (!organizationId) {
    throw new Error('Organization ID not found');
  }

  try {
    const draftNumber = await generateDraftNumber(organizationId);
    
    const draftData = {
      ...proformaData,
      organizationId,
      draftNumber,
      status: 'DRAFT',
      createdAt: new Date(),
      updatedAt: new Date(),
      invoiceType: 'PROFORMA',
      // Flatten buyer data for easier querying
      buyerName: proformaData.buyer?.name || 'Unknown buyer',
      buyerEmail: proformaData.buyer?.email || '',
      buyerTaxNumber: proformaData.buyer?.taxNumber || '',
      buyerPhone: proformaData.buyer?.phone || '',
      buyerAddress: proformaData.buyer?.address ? 
        `${proformaData.buyer.address.street}, ${proformaData.buyer.address.city}, ${proformaData.buyer.address.postalCode}` 
        : '',
      // Calculate totals
      netAmount: calculateNetAmount(proformaData.items),
      grossAmount: calculateGrossAmount(proformaData.items),
    };

    const docRef = await addDoc(collection(db, 'invoiceDrafts'), draftData);
    
    // Generate warranties if requested
    if (proformaData.generateWarranty && proformaData.items && proformaData.items.length > 0) {
      await generateWarrantiesForInvoice(docRef.id, proformaData, organizationId);
    }
    
    return docRef.id;
  } catch (error) {
    console.error('Error saving draft proforma:', error);
    throw new Error(`Failed to save draft proforma: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const updateDraftInvoice = async (draftId: string, invoiceData: InvoiceRequest, organizationId: string): Promise<void> => {
  if (!organizationId || !draftId) {
    throw new Error('Organization ID and Draft ID are required');
  }

  try {
    const draftRef = doc(db, 'invoiceDrafts', draftId);
    const updateData = {
      ...invoiceData,
      organizationId,
      updatedAt: new Date(),
      // Flatten buyer data for easier querying
      buyerName: invoiceData.buyer?.name || 'Unknown buyer',
      buyerEmail: invoiceData.buyer?.email || '',
      buyerTaxNumber: invoiceData.buyer?.taxNumber || '',
      buyerPhone: invoiceData.buyer?.phone || '',
      buyerAddress: invoiceData.buyer?.address ? 
        `${invoiceData.buyer.address.street}, ${invoiceData.buyer.address.city}, ${invoiceData.buyer.address.postalCode}` 
        : '',
      // Calculate totals
      netAmount: calculateNetAmount(invoiceData.items),
      grossAmount: calculateGrossAmount(invoiceData.items),
    };

    await updateDoc(draftRef, updateData);
    
    // Generate warranties if requested and not generated before
    if (invoiceData.generateWarranty && invoiceData.items && invoiceData.items.length > 0) {
      // Check if warranties already exist for this draft to avoid duplicates
      const existingWarranties = query(
        collection(db, 'warranties'),
        where('organizationId', '==', organizationId),
        where('invoiceNumber', '==', `DRAFT-${draftId}`)
      );
      
      const existingSnapshot = await getDocs(existingWarranties);
      
      if (existingSnapshot.empty) {
        await generateWarrantiesForInvoice(draftId, invoiceData, organizationId);
      }
    }
  } catch (error) {
    console.error('Error updating draft invoice:', error);
    throw new Error(`Failed to update draft invoice: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const getDraftInvoice = async (draftId: string, organizationId: string): Promise<any> => {
  if (!organizationId || !draftId) {
    throw new Error('Organization ID and Draft ID are required');
  }

  try {
    const draftRef = doc(db, 'invoiceDrafts', draftId);
    const draftSnap = await getDoc(draftRef);

    if (!draftSnap.exists()) {
      throw new Error('Draft not found');
    }

    const draftData = draftSnap.data();
    
    // Check if user has access to this draft
    if (draftData.organizationId !== organizationId) {
      throw new Error('Access denied');
    }

    return {
      id: draftSnap.id,
      ...draftData
    };
  } catch (error) {
    console.error('Error getting draft invoice:', error);
    throw new Error(`Failed to get draft invoice: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const deleteDraftInvoice = async (draftId: string, organizationId: string): Promise<void> => {
  if (!organizationId || !draftId) {
    throw new Error('Organization ID and Draft ID are required');
  }

  try {
    // First verify the draft belongs to this organization
    const draftData = await getDraftInvoice(draftId, organizationId);
    
    const draftRef = doc(db, 'invoiceDrafts', draftId);
    await deleteDoc(draftRef);
  } catch (error) {
    console.error('Error deleting draft invoice:', error);
    throw new Error(`Failed to delete draft invoice: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const generateLiveInvoice = async (draftId: string, organizationId: string, isTest: boolean = false): Promise<void> => {
  if (!organizationId || !draftId) {
    throw new Error('Organization ID and Draft ID are required');
  }

  // Check if this is a draft (should start with 'draft_')
  if (!draftId.startsWith('draft_')) {
    throw new Error('This invoice is not a draft. Only draft invoices can be converted to live invoices.');
  }

  try {
    // Remove the 'draft_' prefix to get the actual Firestore document ID
    const actualDraftId = draftId.replace('draft_', '');
    
    // First check if this is actually a draft
    const draftRef = doc(db, 'invoiceDrafts', actualDraftId);
    const draftSnap = await getDoc(draftRef);
    
    if (!draftSnap.exists()) {
      throw new Error('Draft not found. Only draft invoices can be converted to live invoices.');
    }
    
    // Get the draft data
    const draftData = await getDraftInvoice(actualDraftId, organizationId);
    
    if (isTest) {
      // Transform draft data for preview as well
      const transformedData = {
        buyer: {
          name: draftData.buyerName,
          email: draftData.buyerEmail,
          phone: draftData.buyerPhone,
          taxNumber: draftData.buyerTaxNumber,
          address: draftData.buyerAddress ? {
            street: draftData.buyerAddress.split(',')[0]?.trim() || '',
            city: draftData.buyerAddress.split(',')[1]?.trim() || '',
            postalCode: draftData.buyerAddress.split(',')[2]?.trim() || '',
            country: 'Magyarország'
          } : undefined
        },
        items: draftData.items || [],
        paymentMethod: draftData.paymentMethod || 'TRANSFER',
        paymentDeadline: draftData.paymentDeadline || 8,
        currency: draftData.currency || 'HUF',
        notes: draftData.notes || '',
        organizationId: organizationId,
        invoiceType: draftData.invoiceType || 'INVOICE'
      };
      
      // Generate test/preview version
      const htmlPreview = await generateHTMLPreview(transformedData, organizationId);
      
      // Open in new window
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(htmlPreview);
        newWindow.document.close();
      }
    } else {
      // Transform draft data to Cloud Function expected format
      console.log('Original draft data:', draftData);
      const transformedData = {
        buyer: {
          name: draftData.buyerName,
          email: draftData.buyerEmail,
          phone: draftData.buyerPhone,
          taxNumber: draftData.buyerTaxNumber,
          address: draftData.buyerAddress ? {
            street: draftData.buyerAddress.split(',')[0]?.trim() || '',
            city: draftData.buyerAddress.split(',')[1]?.trim() || '',
            postalCode: draftData.buyerAddress.split(',')[2]?.trim() || '',
            country: 'Magyarország'
          } : undefined
        },
        items: draftData.items || [],
        paymentMethod: draftData.paymentMethod || 'TRANSFER',
        paymentDeadline: draftData.paymentDeadline || 8,
        currency: draftData.currency || 'HUF',
        notes: draftData.notes || '',
        organizationId: organizationId,
        invoiceType: draftData.invoiceType || 'INVOICE',
        ...(draftData.invoiceType === 'PROFORMA' && draftData.validUntil && {
          validUntil: draftData.validUntil
        })
             };
       
       console.log('Transformed data for Cloud Function:', transformedData);
       console.log('Items in transformedData:', transformedData.items);
       console.log('Buyer in transformedData:', transformedData.buyer);
       
       // Validate required data before sending to Cloud Function
       if (!transformedData.items || transformedData.items.length === 0) {
         throw new Error('Nincsenek tételek a számlában. Legalább egy tétel szükséges a számla generálásához.');
       }
       
       if (!transformedData.buyer?.name) {
         throw new Error('Vevő neve nincs megadva. A vevő neve kötelező.');
       }
       
       // Generate live invoice using the appropriate function
      if (draftData.invoiceType === 'PROFORMA') {
        await createProforma(transformedData, organizationId);
      } else {
        await createInvoice(transformedData, organizationId);
      }
      
      // Optionally delete the draft after generating live version
      await deleteDraftInvoice(actualDraftId, organizationId);
    }
  } catch (error) {
    console.error('Error generating live invoice:', error);
    throw new Error(`Failed to generate live invoice: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Updated listInvoices to include both live invoices and drafts using direct Firestore access
export const listInvoices = async (organizationId: string): Promise<InvoiceListItem[]> => {
  if (!organizationId) {
    throw new Error('Organization ID not found');
  }

  try {
    // Get live invoices directly from Firestore
    let liveInvoices: InvoiceListItem[] = [];
    try {
      const invoicesQuery = query(
        collection(db, 'invoices'),
        where('organizationId', '==', organizationId),
        orderBy('createdAt', 'desc')
      );
      
      const invoicesSnapshot = await getDocs(invoicesQuery);
      
      liveInvoices = invoicesSnapshot.docs.map(doc => {
        const invoice = doc.data();
        console.log('Processing live invoice:', doc.id, 'status:', invoice.status);
        return {
          id: `live_${doc.id}`, // Add prefix to differentiate from drafts
          number: invoice.invoiceNumber || `LIVE-${doc.id.slice(-6)}`,
          type: invoice.invoiceType || 'INVOICE',
          buyerName: invoice.buyerName || 'Unknown buyer',
          buyerEmail: invoice.buyerEmail || '',
          totalAmount: invoice.grossAmount || 0,
          issueDate: invoice.issueDate?.toDate?.() || new Date(invoice.issueDate || Date.now()),
          dueDate: invoice.dueDate?.toDate?.() || new Date(invoice.dueDate || Date.now()),
          paid: invoice.status === 'PAID',
          status: invoice.status === 'PAID' ? 'PAID' : 'UNPAID', // Ensure live invoices never have DRAFT status
          currency: invoice.currency || 'HUF'
        };
      });
      
      console.log('Live invoices mapped:', liveInvoices.length);
    } catch (error) {
      console.error('Error getting live invoices:', error);
      // Continue with empty live invoices array
    }

    // Get draft invoices from Firestore
    let draftInvoices: InvoiceListItem[] = [];
    try {
      const draftsQuery = query(
        collection(db, 'invoiceDrafts'),
        where('organizationId', '==', organizationId)
      );
      
      const draftsSnapshot = await getDocs(draftsQuery);
      console.log('Total drafts for organization:', draftsSnapshot.docs.length);
      
      draftInvoices = draftsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: `draft_${doc.id}`, // Add prefix to differentiate from live invoices
          number: data.draftNumber || `DRAFT-${doc.id.slice(-6)}`, // Use generated draft number or fallback
          type: data.invoiceType || 'INVOICE',
          buyerName: data.buyerName || 'Unknown buyer',
          buyerEmail: data.buyerEmail || '',
          totalAmount: data.grossAmount || calculateGrossAmount(data.items || []),
          issueDate: data.issueDate instanceof Date ? data.issueDate : new Date(data.issueDate || Date.now()),
          dueDate: data.dueDate instanceof Date ? data.dueDate : new Date(data.dueDate || Date.now()),
          paid: false,
          status: 'DRAFT',
          currency: data.currency || 'HUF'
        };
      });
    } catch (error) {
      console.error('Error getting draft invoices:', error);
      // Continue with empty draft invoices array
    }

    // Combine and sort all invoices by creation date (newest first)
    const allInvoices = [...liveInvoices, ...draftInvoices];
    console.log('Combined invoices:', allInvoices.length, 'Live:', liveInvoices.length, 'Drafts:', draftInvoices.length);
    return allInvoices.sort((a, b) => b.issueDate.getTime() - a.issueDate.getTime());
    
  } catch (error) {
    console.error('Error listing invoices:', error);
    // Return empty array instead of throwing error to prevent UI crash
    return [];
  }
};

export const downloadInvoice = async (invoiceNumber: string, organizationId: string): Promise<string> => {
  if (!organizationId || !invoiceNumber) {
    throw new Error('Organization ID and Invoice Number are required');
  }

  try {
    const result = await downloadInvoiceFunction({ 
      invoiceNumber, 
      organizationId 
    });
    
    return result.data as string;
  } catch (error) {
    console.error('Error downloading invoice:', error);
    throw new Error(`Failed to download invoice: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

export const generateHTMLPreview = async (invoiceData: InvoiceRequest | ProformaRequest, organizationId: string): Promise<string> => {
  if (!organizationId) {
    throw new Error('Organization ID not found');
  }

  try {
    const result = await generateHTMLPreviewFunction({ invoiceData, organizationId });
    return result.data as string;
  } catch (error) {
    console.error('Error generating HTML preview:', error);
    throw new Error(`Failed to generate preview: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

// Helper function for opening printable invoice in new tab
export const openPrintableInvoice = (invoiceData: any): void => {
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('hu-HU', { style: 'currency', currency: 'HUF' }).format(amount);
  };

  const formatDate = (date: Date): string => {
    return new Intl.DateTimeFormat('hu-HU').format(date);
  };

  const html = `<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>${invoiceData.type === 'PROFORMA' ? 'Díjbekérő' : 'Számla'} - ${invoiceData.number}</title>
    <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: Arial, sans-serif; font-size: 12px; line-height: 1.4; color: #333; background: white; }
        .container { max-width: 800px; margin: 0 auto; padding: 20px; }
        .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 30px; border-bottom: 2px solid #2563eb; padding-bottom: 20px; }
        .logo { font-size: 24px; font-weight: bold; color: #2563eb; }
        .document-info { text-align: right; }
        .document-title { font-size: 20px; font-weight: bold; color: #2563eb; margin-bottom: 10px; }
        .parties { display: flex; justify-content: space-between; margin-bottom: 30px; }
        .party { width: 48%; }
        .party-title { font-weight: bold; color: #2563eb; margin-bottom: 10px; border-bottom: 1px solid #e5e7eb; padding-bottom: 5px; }
        .items-table { width: 100%; border-collapse: collapse; margin-bottom: 30px; }
        .items-table th, .items-table td { border: 1px solid #d1d5db; padding: 8px; text-align: left; }
        .items-table th { background-color: #f3f4f6; font-weight: bold; color: #374151; }
        .items-table .number { text-align: right; }
        .summary { margin-bottom: 30px; }
        .summary-table { width: 100%; max-width: 400px; margin-left: auto; border-collapse: collapse; }
        .summary-table td { border: 1px solid #d1d5db; padding: 8px; }
        .summary-table .total { background-color: #2563eb; color: white; font-weight: bold; }
        .footer { border-top: 2px solid #2563eb; padding-top: 20px; display: flex; justify-content: space-between; }
        .payment-info, .notes { width: 48%; }
        .section-title { font-weight: bold; color: #2563eb; margin-bottom: 10px; }
        @media print {
            body { print-color-adjust: exact; }
            .container { margin: 0; padding: 15px; max-width: none; }
            .no-print { display: none; }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            <div class="logo">YouCRM</div>
            <div class="document-info">
                <div class="document-title">${invoiceData.type === 'PROFORMA' ? 'DÍJBEKÉRŐ' : 'SZÁMLA'}</div>
                <div><strong>Számlaszám:</strong> ${invoiceData.number}</div>
                <div><strong>Kiállítás dátuma:</strong> ${formatDate(invoiceData.issueDate)}</div>
                <div><strong>Fizetési határidő:</strong> ${formatDate(invoiceData.dueDate)}</div>
            </div>
        </div>

        <div class="parties">
            <div class="party">
                <div class="party-title">Eladó / Service Provider</div>
                <div><strong>${invoiceData.issuer?.companyName || 'Cégnem'}</strong></div>
                <div>${invoiceData.issuer?.address || 'Address not available'}</div>
                <div>Adószám: ${invoiceData.issuer?.taxNumber || 'N/A'}</div>
                <div>Email: ${invoiceData.issuer?.email || 'N/A'}</div>
                <div>Tel: ${invoiceData.issuer?.phone || 'N/A'}</div>
            </div>
            <div class="party">
                <div class="party-title">Vevő / Buyer</div>
                <div><strong>${invoiceData.buyerName}</strong></div>
                <div>${invoiceData.buyerAddress}</div>
                ${invoiceData.buyerTaxNumber ? `<div>Adószám: ${invoiceData.buyerTaxNumber}</div>` : ''}
                ${invoiceData.buyerEmail ? `<div>Email: ${invoiceData.buyerEmail}</div>` : ''}
                ${invoiceData.buyerPhone ? `<div>Tel: ${invoiceData.buyerPhone}</div>` : ''}
            </div>
        </div>

        <table class="items-table">
            <thead>
                <tr>
                    <th>Megnevezés</th>
                    <th>Mennyiség</th>
                    <th>Mértékegység</th>
                    <th>Egységár (nettó)</th>
                    <th>Nettó érték</th>
                    <th>ÁFA %</th>
                    <th>ÁFA összeg</th>
                    <th>Bruttó érték</th>
                </tr>
            </thead>
            <tbody>
                ${invoiceData.items.map((item: any) => `
                    <tr>
                        <td>${item.name}</td>
                        <td class="number">${item.quantity}</td>
                        <td>${item.unit}</td>
                        <td class="number">${formatCurrency(item.netUnitPrice || 0)}</td>
                        <td class="number">${formatCurrency(item.netValue || 0)}</td>
                        <td class="number">${item.vatRate}%</td>
                        <td class="number">${formatCurrency((item.grossValue || 0) - (item.netValue || 0))}</td>
                        <td class="number">${formatCurrency(item.grossValue || 0)}</td>
                    </tr>
                `).join('')}
            </tbody>
        </table>

        <div class="summary">
            <table class="summary-table">
                <tr>
                    <td><strong>Nettó összesen:</strong></td>
                    <td class="number"><strong>${formatCurrency(invoiceData.netAmount)}</strong></td>
                </tr>
                <tr>
                    <td><strong>ÁFA összesen:</strong></td>
                    <td class="number"><strong>${formatCurrency(invoiceData.grossAmount - invoiceData.netAmount)}</strong></td>
                </tr>
                <tr class="total">
                    <td><strong>Fizetendő összesen:</strong></td>
                    <td class="number"><strong>${formatCurrency(invoiceData.grossAmount)}</strong></td>
                </tr>
            </table>
        </div>

        <div class="footer">
            <div class="payment-info">
                <div class="section-title">Fizetési információk</div>
                <div><strong>Fizetési mód:</strong> ${invoiceData.paymentMethod}</div>
                <div><strong>Pénznem:</strong> ${invoiceData.currency}</div>
            </div>
            <div class="notes">
                <div class="section-title">Megjegyzések</div>
                <div>${invoiceData.notes || 'Nincs megjegyzés'}</div>
            </div>
        </div>
    </div>
    <script>
        window.onload = function() {
            window.print();
        };
    </script>
</body>
</html>`;

  const newWindow = window.open('', '_blank');
  if (newWindow) {
    newWindow.document.write(html);
    newWindow.document.close();
  }
}; 